import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import NavBar from "@/components/NavBar";
import OrderDialog from "@/components/OrderDialog";
import ImageLightbox from "@/components/ImageLightbox";
import Footer from "@/components/sections/Footer";
import { useLang } from "@/context/LanguageContext";
import { getProductBySlug } from "@/lib/api";

/* ------------ Per-product copy (EN/IT) ------------ */
const COPY = {
  "front-lip": {
    en: {
      title1: "CHASSIS MOUNT",
      title2: "FRONT LIP",
      description:
        "Designed to withstand real track use. The CRVD Chassis Mount Front Lip is engineered for drivers who demand more than a cosmetic front splitter. Unlike traditional bumper-mounted solutions, the CRVD system is mounted directly to the chassis, providing a significantly stronger and more stable platform capable of handling the loads generated during high-speed driving and track use. Developed specifically for the BMW F8X platform, the chassis-mounted design minimises flex, improves durability and ensures that aerodynamic loads are transferred directly into the vehicle structure rather than the bumper assembly. Constructed from motorsport-proven 9-ply birch plywood, the lip offers an excellent balance of strength, impact resistance and serviceability — repair or replace it after curb contact without compromising the mounting system. Built to withstand real-world track abuse.",
      specs: [
        ["SKU", "CR-CMFL-F8X"],
        ["MATERIAL", "9-ply birch plywood, motorsport-grade"],
        ["THICKNESS", "15 mm"],
        ["WEIGHT", "3.6 kg"],
        ["LENGTH", "1700 mm"],
        ["DEPTH", "500 mm"],
        ["MOUNT", "Chassis-mounted · brackets + hardware"],
        ["FIT", "F80 M3 · F82/F83 M4"],
        ["ORIGIN", "CNC Milan · Italy"],
      ],
      box: [
        "1× CRVD Chassis Mount Front Lip (9-ply birch plywood)",
        "Chassis mounting brackets",
        "Mounting hardware (4 bolts, 4 bushings, 2 rivets)",
        "Installation instructions",
      ],
    },
    it: {
      title1: "CHASSIS MOUNT",
      title2: "FRONT LIP",
      description:
        "Progettato per resistere al vero uso in pista. Il CRVD Chassis Mount Front Lip è pensato per chi pretende più di un semplice splitter estetico. A differenza dei sistemi tradizionali montati al paraurti, il CRVD è fissato direttamente al telaio: una base molto più solida e stabile, capace di reggere i carichi generati ad alta velocità e in pista. Sviluppato specificamente per la piattaforma BMW F8X, il design chassis-mount minimizza la flessione, migliora la durabilità e trasferisce i carichi aerodinamici direttamente sulla struttura della vettura invece che sul paraurti. Costruito in compensato di betulla a 9 strati di derivazione motorsport, il labbro offre un eccellente equilibrio tra resistenza, assorbimento degli urti e riparabilità — in caso di contatto con cordoli o asfalto puoi riparare o sostituire la lama senza compromettere il sistema di fissaggio. Costruito per reggere l'uso reale in pista.",
      specs: [
        ["SKU", "CR-CMFL-F8X"],
        ["MATERIALE", "Compensato betulla 9 strati, motorsport"],
        ["SPESSORE", "15 mm"],
        ["PESO", "3,6 kg"],
        ["LUNGHEZZA", "1700 mm"],
        ["PROFONDITÀ", "500 mm"],
        ["FISSAGGIO", "Chassis-mount · staffe + ferramenta"],
        ["COMPATIBILITÀ", "F80 M3 · F82/F83 M4"],
        ["ORIGINE", "CNC Milano · Italia"],
      ],
      box: [
        "1× CRVD Chassis Mount Front Lip (compensato betulla 9 strati)",
        "Staffe di fissaggio al telaio",
        "Ferramenta (4 bulloni, 4 boccole, 2 rivetti)",
        "Istruzioni di installazione",
      ],
    },
  },
  "diff-mount": {
    en: {
      title1: "DUAL DIFF",
      title2: "MOUNT",
      description:
        "Increased differential support. Improved drivetrain control. The CRVD Dual Mount Differential Kit was developed to address one of the key limitations of the factory differential mounting system on the BMW F8X platform. Under hard acceleration, aggressive gear changes and high-grip track conditions, the rear differential is subjected to significant loads that can lead to unwanted movement within the drivetrain. By introducing an additional mounting point, this kit increases overall support and better controls differential movement under load — for a more connected driving experience, improved drivetrain stability and greater confidence when pushing the car to its limits. Direct-fit for F80 M3, F82/F83 M4. Suitable for road and track.",
      specs: [
        ["SKU", "CR-DDM-F8X"],
        ["MATERIAL", "Aluminium 7075-T6"],
        ["LIFT BUSHINGS", "Aluminium 7075-T6 (eccentric)"],
        ["LARGE BUSHINGS", "Configurable: PU / PVC / Aluminium"],
        ["WEIGHT", "2.1 kg"],
        ["FINISH", "Matte black anodize"],
        ["MOUNT", "Direct bolt-on · OEM points"],
        ["ORIGIN", "CNC Milan · Italy"],
      ],
      box: [
        "1× CRVD Dual Mount differential bracket (CNC aluminium 7075-T6)",
        "2× eccentric lift bushings (aluminium 7075-T6)",
        "2× large bushings (your selected material)",
        "High-strength mounting hardware",
        "Installation instructions",
      ],
    },
    it: {
      title1: "DUAL DIFF",
      title2: "MOUNT",
      description:
        "Maggior supporto al differenziale. Miglior controllo della trasmissione. Il CRVD Dual Mount Differential Kit è stato sviluppato per superare uno dei limiti principali del sistema di fissaggio del differenziale di serie sulla piattaforma BMW F8X. In accelerazione spinta, cambi marcia aggressivi e condizioni di pista ad alto grip, il differenziale posteriore è sottoposto a carichi importanti che possono generare movimenti indesiderati nella trasmissione. Il kit introduce un punto di fissaggio aggiuntivo, aumentando il supporto complessivo e controllando meglio il movimento del differenziale sotto carico — per una guida più connessa, una trasmissione più stabile e più fiducia quando spingi l'auto al limite. Bolt-on diretto per F80 M3, F82/F83 M4. Adatto a strada e pista.",
      specs: [
        ["SKU", "CR-DDM-F8X"],
        ["MATERIALE", "Alluminio 7075-T6"],
        ["BOCCOLE LIFT", "Alluminio 7075-T6 (eccentriche)"],
        ["BOCCOLE GRANDI", "Configurabili: PU / PVC / Alluminio"],
        ["PESO", "2,1 kg"],
        ["FINITURA", "Nero anodizzato opaco"],
        ["FISSAGGIO", "Bolt-on diretto · punti OEM"],
        ["ORIGINE", "CNC Milano · Italia"],
      ],
      box: [
        "1× staffa CRVD Dual Mount differenziale (CNC alluminio 7075-T6)",
        "2× boccole lift eccentriche (alluminio 7075-T6)",
        "2× boccole grandi (materiale scelto)",
        "Ferramenta ad alta resistenza",
        "Istruzioni di installazione",
      ],
    },
  },
  "brake-ducts": {
    en: {
      title1: "BRAKE COOLING",
      title2: "DUCTS",
      description:
        "Purpose-built brake cooling for demanding track conditions. The CRVD Brake Cooling Ducts deliver a focused airflow path from the front bumper directly toward the braking system, helping reduce heat buildup during repeated high-speed braking. Developed specifically for the BMW F8x platform with OEM-like integration. Whether you're chasing lap times or running extended track sessions, effective brake cooling is one of the most important upgrades for consistent braking performance.",
      specs: [
        ["SKU", "CR-BCD-F8X"],
        ["FITMENT", "F80 M3 · F82/F83 M4"],
        ["FIT", "Direct-fit, OEM-style"],
        ["WEIGHT", "0.9 kg (pair)"],
        ["FINISH", "Matte black"],
        ["USE", "Track / high-performance"],
        ["ORIGIN", "Milan · Italy"],
      ],
      box: [
        "1× left brake cooling duct",
        "1× right brake cooling duct",
        "Mounting hardware",
        "Installation gaskets",
      ],
    },
    it: {
      title1: "BRAKE COOLING",
      title2: "DUCTS",
      description:
        "Raffreddamento freni progettato per condizioni in pista esigenti. I CRVD Brake Cooling Ducts convogliano un flusso d'aria mirato dal paraurti anteriore direttamente verso l'impianto frenante, riducendo l'accumulo di calore durante frenate ripetute ad alta velocità. Sviluppati specificamente per la piattaforma BMW F8x con integrazione OEM-style. Che tu stia cercando di abbattere i tempi sul giro o di affrontare lunghe sessioni in pista, il raffreddamento dei freni è uno degli upgrade più importanti per una frenata costante.",
      specs: [
        ["SKU", "CR-BCD-F8X"],
        ["COMPATIBILITÀ", "F80 M3 · F82/F83 M4"],
        ["MONTAGGIO", "Direct-fit, OEM-style"],
        ["PESO", "0,9 kg (coppia)"],
        ["FINITURA", "Nero opaco"],
        ["USO", "Pista / alte prestazioni"],
        ["ORIGINE", "Milano · Italia"],
      ],
      box: [
        "1× condotto raffreddamento freno sinistro",
        "1× condotto raffreddamento freno destro",
        "Ferramenta di montaggio",
        "Guarnizioni di installazione",
      ],
    },
  },
  "seat-delete": {
    en: {
      title1: "SEAT DELETE",
      title2: "KIT",
      description:
        "A pure track-focused interior solution for the BMW F8X. The CRVD Seat Delete Kit replaces the OEM rear seats with a lightweight, custom-trimmed seat base + backrest assembly — saving weight, opening up roll-cage clearance and giving the cabin a true motorsport look. Each kit is hand-trimmed in Italy in Alcantara, leather or carbon fiber, with concealed fixings to OEM mounting points. Designed for drivers who actually use their car on track.",
      specs: [
        ["SKU", "CR-SDK-F8X"],
        ["FITMENT", "F87 M2 · F80 M3 · F82/F83 M4"],
        ["INCLUDES", "Seat base + backrest"],
        ["MATERIALS", "Alcantara · Leather · Carbon"],
        ["WEIGHT SAVING", "~12 kg vs OEM seats"],
        ["MOUNT", "OEM points · no drilling"],
        ["BUILD", "Hand-trimmed in Italy"],
        ["ORIGIN", "Milan · Italy"],
      ],
      box: [
        "1× seat base (custom-trimmed)",
        "1× backrest (custom-trimmed)",
        "Mounting hardware",
        "Installation instructions",
      ],
    },
    it: {
      title1: "SEAT DELETE",
      title2: "KIT",
      description:
        "Una soluzione interni pura, pensata per la pista, per BMW F8X. Il CRVD Seat Delete Kit sostituisce i sedili posteriori OEM con un set pianale + schienale leggero e rifinito su misura — togli peso, liberi spazio per roll-bar e dai all'abitacolo un look motorsport vero. Ogni kit è confezionato a mano in Italia in Alcantara, pelle o fibra di carbonio, con fissaggi nascosti sui punti OEM. Pensato per chi davvero porta la macchina in pista.",
      specs: [
        ["SKU", "CR-SDK-F8X"],
        ["COMPATIBILITÀ", "F87 M2 · F80 M3 · F82/F83 M4"],
        ["INCLUDE", "Pianale + schienale"],
        ["MATERIALI", "Alcantara · Pelle · Carbonio"],
        ["RISPARMIO PESO", "~12 kg vs sedili OEM"],
        ["MONTAGGIO", "Punti OEM · nessuna foratura"],
        ["LAVORAZIONE", "Confezionato a mano in Italia"],
        ["ORIGINE", "Milano · Italia"],
      ],
      box: [
        "1× pianale (rifinitura su misura)",
        "1× schienale (rifinitura su misura)",
        "Ferramenta di montaggio",
        "Istruzioni di installazione",
      ],
    },
  },
  "speedholes": {
    en: {
      title1: "SPEEDHOLES MESH",
      title2: "KIT + TEMPLATE",
      description:
        "Motorsport-inspired rear bumper vents — universal fit. Ø65 mm oval speedholes with a matte black honeycomb mesh grille and a 3K carbon fiber trim ring. 3D-printed in high-strength PLA, they add a real track-look to any rear bumper and improve airflow behind the rear wheels. Includes a paper drilling template so you can cut both openings on your bumper perfectly aligned, first try.",
      specs: [
        ["SKU", "CR-AVS-UNI"],
        ["DIAMETER", "Ø 65 mm (oval)"],
        ["MATERIAL", "3D-printed PLA mesh + carbon trim"],
        ["WEIGHT", "150 g (set)"],
        ["FINISH", "Matte black + 3K carbon"],
        ["FITMENT", "Universal · any bumper"],
        ["MOUNT", "Bumper drilling required"],
        ["INCLUDES", "Drilling template"],
        ["ORIGIN", "Milan · Italy"],
      ],
      box: [
        "2× oval honeycomb mesh (3D-printed PLA)",
        "2× carbon fiber trim rings",
        "1× paper drilling template",
        "Installation instructions",
      ],
    },
    it: {
      title1: "SPEEDHOLES MESH",
      title2: "KIT + DIMA",
      description:
        "Sfoghi paraurti posteriore ispirati al motorsport — fit universale. Speedholes ovali Ø65 mm con griglia mesh a nido d'ape nera opaca e cornice in fibra di carbonio 3K. Stampati in PLA ad alta resistenza, aggiungono un vero look track a qualsiasi paraurti posteriore e migliorano il flusso d'aria dietro le ruote. Include una dima cartacea di foratura per tagliare le due aperture sul paraurti perfettamente allineate, al primo colpo.",
      specs: [
        ["SKU", "CR-AVS-UNI"],
        ["DIAMETRO", "Ø 65 mm (ovale)"],
        ["MATERIALE", "Mesh PLA stampato 3D + cornice carbonio"],
        ["PESO", "150 g (set)"],
        ["FINITURA", "Nero opaco + carbonio 3K"],
        ["COMPATIBILITÀ", "Universale · qualsiasi paraurti"],
        ["MONTAGGIO", "Richiede foratura paraurti"],
        ["INCLUSO", "Dima di foratura"],
        ["ORIGINE", "Milano · Italia"],
      ],
      box: [
        "2× griglia mesh a nido d'ape (PLA stampata 3D)",
        "2× cornici in fibra di carbonio",
        "1× dima cartacea di foratura",
        "Istruzioni di installazione",
      ],
    },
  },
};

const UI = {
  en: {
    kicker: "SHOP / MONO PRODUCT",
    stock: "UNITS LEFT",
    alwaysAvailable: "ALWAYS IN STOCK",
    price: "PRICE",
    fitment: "SELECT YOUR CHASSIS",
    qty: "QTY",
    buy: "ORDER NOW",
    ship: "Shipping (tracked)",
    total: "TOTAL",
    back: "← BACK",
    tabSpecs: "SPECS",
    tabBox: "WHAT'S IN THE BOX",
  },
  it: {
    kicker: "SHOP / MONO PRODOTTO",
    stock: "DISPONIBILI",
    alwaysAvailable: "SEMPRE DISPONIBILE",
    price: "PREZZO",
    fitment: "SCEGLI LA TUA VETTURA",
    qty: "QTÀ",
    buy: "ORDINA ORA",
    ship: "Spedizione (tracciata)",
    total: "TOTALE",
    back: "← INDIETRO",
    tabSpecs: "SPEC",
    tabBox: "COSA C'È NELLA SCATOLA",
  },
};

export default function ProductPage() {
  const { slug = "front-lip" } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const [product, setProduct] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [fitment, setFitment] = useState("");
  const [qty, setQty] = useState(1);
  const [orderOpen, setOrderOpen] = useState(false);
  const [tab, setTab] = useState("specs");
  const [zoomSrc, setZoomSrc] = useState(null);
  const [options, setOptions] = useState({});

  useEffect(() => {
    getProductBySlug(slug)
      .then((p) => {
        setProduct(p);
        setFitment(p?.fitments?.[0]?.code || "");
        // initialise options with defaults
        const initOpts = {};
        (p?.options || []).forEach((o) => { initOpts[o.key] = o.default; });
        setOptions(initOpts);
      })
      .catch(() => navigate("/"));
    setActiveImage(0);
  }, [slug, navigate]);

  if (!product) {
    return (
      <div className="min-h-screen bg-ink text-bone flex items-center justify-center">
        <div className="font-mono text-xs tracking-tech text-bone/50">LOADING…</div>
      </div>
    );
  }

  const copy = COPY[slug]?.[lang] || COPY[slug]?.en;
  const L = UI[lang];

  // Compute price including selected options' price_delta
  const optionDelta = (product.options || []).reduce((sum, opt) => {
    const selected = opt.choices.find((c) => c.value === options[opt.key]);
    return sum + (selected?.price_delta || 0);
  }, 0);
  const price = product.price_eur + optionDelta;
  const stock = product.current_stock;
  const shipping = product.shipping_eur;
  const total = price * qty + shipping;
  const gallery = product.gallery || [];
  const activeSrc = gallery[activeImage] || gallery[0];

  return (
    <div className="min-h-screen bg-ink text-bone">
      <NavBar />

      <div className="pt-24 md:pt-28" />

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16">
        <Link
          to="/"
          data-testid="product-back-link"
          className="inline-block font-mono text-[10px] tracking-tech text-bone/50 hover:text-wood mb-6"
        >
          {L.back}
        </Link>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <motion.div
              key={activeSrc}
              initial={{ opacity: 0.4 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="relative aspect-[4/3] border border-[#222] bg-[#0a0a0a] overflow-hidden corner-ticks cursor-zoom-in group"
              data-testid="product-main-image"
              onClick={() => setZoomSrc(activeSrc)}
              role="button"
              aria-label="Zoom image"
            >
              <div className="absolute inset-0 blueprint-grid opacity-15" />
              <img
                src={activeSrc}
                alt={product.name}
                className="absolute inset-0 w-full h-full object-contain p-10 md:p-16 transition-transform duration-500 group-hover:scale-[1.02]"
              />
              <div className="absolute top-4 right-4 font-mono text-[10px] tracking-tech text-wood bg-ink/80 border border-wood/40 px-3 py-1.5 z-20">
                {product.subtitle}
              </div>
              <div className="absolute bottom-4 right-4 font-mono text-[10px] tracking-tech text-bone/60 bg-ink/70 border border-[#333] px-3 py-1.5 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
                ⊕ ZOOM
              </div>
            </motion.div>

            {gallery.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-4">
                {gallery.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImage(i)}
                    data-testid={`gallery-thumb-${i}`}
                    className={`aspect-square border bg-[#0a0a0a] overflow-hidden transition-colors ${
                      activeImage === i ? "border-wood" : "border-[#222] hover:border-bone/40"
                    }`}
                  >
                    <img src={src} alt="" className="w-full h-full object-contain p-2" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: info + buy */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-wood" />
              <span className="font-mono text-[11px] tracking-tech text-wood">{L.kicker}</span>
            </div>

            <h1 className="font-display tracking-brutal text-4xl md:text-5xl lg:text-6xl uppercase leading-[0.95]">
              {copy.title1}<br />
              <span className="text-wood">{copy.title2}</span>
            </h1>
            <div className="mt-3 font-mono text-xs tracking-tech text-bone/60">
              {product.subtitle}
            </div>

            <p className="mt-6 font-sans text-sm md:text-base text-bone/70 leading-relaxed">
              {copy.description}
            </p>

            {/* Price block */}
            <div className="mt-8 border border-[#333] bg-steel/30 p-6">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <div className="font-mono text-[10px] tracking-tech text-bone/50">{L.price}</div>
                  <div className="font-display text-6xl md:text-7xl text-wood leading-none mt-1">
                    €{price}
                  </div>
                </div>
                <div className="text-right">
                  {product.always_in_stock ? (
                    <div className="flex items-center gap-2 justify-end" data-testid="stock-always-available">
                      <span className="w-2 h-2 bg-wood animate-pulse" />
                      <span className="font-mono text-[10px] tracking-tech text-wood">{L.alwaysAvailable}</span>
                    </div>
                  ) : (
                    <>
                      <div className="font-mono text-[10px] tracking-tech text-danger">{L.stock}</div>
                      <div className="font-display text-3xl text-danger">{stock}/{product.stock_cap}</div>
                    </>
                  )}
                </div>
              </div>

              {/* Fitment */}
              <div className="mt-6">
                <div className="font-mono text-[10px] tracking-tech text-bone/60 mb-2">
                  {L.fitment}
                </div>
                <div className={`grid gap-2 ${product.fitments.length <= 3 ? "grid-cols-3" : "grid-cols-3 md:grid-cols-6"}`}>
                  {product.fitments.map((f) => (
                    <button
                      key={f.code}
                      onClick={() => setFitment(f.code)}
                      data-testid={`fitment-${f.code}`}
                      className={`font-mono text-[11px] tracking-tech py-3 border transition-colors ${
                        fitment === f.code
                          ? "border-wood bg-wood/10 text-wood"
                          : "border-[#333] text-bone/60 hover:border-bone/40"
                      }`}
                    >
                      {f.code}
                    </button>
                  ))}
                </div>
                <div className="font-mono text-[10px] text-bone/40 mt-2">
                  {product.fitments.find((x) => x.code === fitment)?.label || ""}
                </div>
              </div>

              {/* Options (custom configurable fields per product) */}
              {(product.options || []).map((opt) => {
                const optLabel = lang === "it" ? opt.label_it : opt.label_en;
                return (
                  <div className="mt-6" key={opt.key}>
                    <div className="font-mono text-[10px] tracking-tech text-bone/60 mb-2">
                      {optLabel}
                    </div>
                    <div className={`grid gap-2 grid-cols-${opt.choices.length}`} style={{ gridTemplateColumns: `repeat(${opt.choices.length}, minmax(0, 1fr))` }}>
                      {opt.choices.map((c) => {
                        const cLabel = lang === "it" ? c.label_it : c.label_en;
                        const active = options[opt.key] === c.value;
                        return (
                          <button
                            key={c.value}
                            onClick={() => setOptions((o) => ({ ...o, [opt.key]: c.value }))}
                            data-testid={`option-${opt.key}-${c.value}`}
                            className={`font-mono text-[11px] tracking-tech py-3 px-2 border transition-colors ${
                              active
                                ? "border-wood bg-wood/10 text-wood"
                                : "border-[#333] text-bone/60 hover:border-bone/40"
                            }`}
                          >
                            {cLabel}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Custom personalization note (Instagram DM) */}
              {(product.custom_note_en || product.custom_note_it) && (
                <a
                  href="https://instagram.com/runcrvd"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="custom-note-instagram"
                  className="mt-5 block border border-wood/40 bg-wood/5 p-4 hover:bg-wood/10 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <span className="font-mono text-[10px] tracking-tech text-wood mt-0.5">DM →</span>
                    <p className="font-mono text-[11px] leading-relaxed text-bone/80">
                      {lang === "it" ? product.custom_note_it : product.custom_note_en}
                    </p>
                  </div>
                </a>
              )}

              {/* Qty + totals */}
              <div className="mt-6 flex items-end gap-4">
                <div>
                  <div className="font-mono text-[10px] tracking-tech text-bone/60 mb-2">{L.qty}</div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                      data-testid="qty-minus"
                      className="w-10 h-10 border border-[#333] text-bone hover:border-wood hover:text-wood"
                    >−</button>
                    <div className="font-display text-2xl w-12 text-center">{qty}</div>
                    <button
                      onClick={() => setQty((q) => Math.min(5, q + 1))}
                      data-testid="qty-plus"
                      className="w-10 h-10 border border-[#333] text-bone hover:border-wood hover:text-wood"
                    >+</button>
                  </div>
                </div>
                <div className="flex-1 border border-[#333] p-3">
                  <div className="font-mono text-[10px] tracking-tech text-bone/50">{L.total}</div>
                  <div className="font-display text-3xl text-wood mt-0.5">€{total}</div>
                  <div className="font-mono text-[9px] text-bone/40 mt-1">
                    + €{shipping} {L.ship}
                  </div>
                </div>
              </div>

              <button
                onClick={() => setOrderOpen(true)}
                data-testid="product-buy-button"
                disabled={stock === 0 || !fitment}
                className="w-full mt-6 bg-wood text-ink font-mono text-sm tracking-tech py-5 hover:bg-bone transition-colors disabled:opacity-40"
              >
                {L.buy} →
              </button>
            </div>
          </div>
        </div>

        {/* Tabs: Specs + Box */}
        <div className="mt-16 border-t border-[#222]">
          <div className="flex border-b border-[#222]">
            {[
              { id: "specs", label: L.tabSpecs },
              { id: "box", label: L.tabBox },
            ].map((tb) => (
              <button
                key={tb.id}
                onClick={() => setTab(tb.id)}
                data-testid={`product-tab-${tb.id}`}
                className={`font-mono text-xs tracking-tech px-6 py-4 border-b-2 ${
                  tab === tb.id
                    ? "border-wood text-wood"
                    : "border-transparent text-bone/50 hover:text-bone"
                }`}
              >
                {tb.label}
              </button>
            ))}
          </div>

          <div className="py-10">
            {tab === "specs" && (
              <div className="grid md:grid-cols-2 gap-0 border-l border-t border-[#222]">
                {copy.specs.map(([k, v], i) => (
                  <div
                    key={i}
                    className="border-r border-b border-[#222] px-6 py-4 flex justify-between items-center font-mono text-xs"
                  >
                    <span className="text-bone/50 tracking-tech">{k}</span>
                    <span className="text-bone">{v}</span>
                  </div>
                ))}
              </div>
            )}
            {tab === "box" && (
              <ul className="space-y-3 max-w-2xl">
                {copy.box.map((b, i) => (
                  <li key={i} className="flex gap-4 font-mono text-sm">
                    <span className="text-wood">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-bone/80">{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <Footer />

      <OrderDialog
        open={orderOpen}
        onOpenChange={setOrderOpen}
        product={product}
        fitment={fitment}
        quantity={qty}
        options={options}
      />

      <ImageLightbox src={zoomSrc} alt={product.name} onClose={() => setZoomSrc(null)} />
    </div>
  );
}
